import React, { useCallback, useEffect, useMemo, useState } from 'react';
import APIService from '../services/api';
import { ancre } from '../tutoriel/ancres';

/**
 * Contrôle qualité d'un dossier AO (04.11, vécu du dossier 831).
 *
 * « Un checker de qualité dans la vue dossier AO, qui va checker le dossier
 * partagé et donner un rapport. » Le contrôle est calculé par le SERVEUR
 * (utils/aoControleQualite.js) : cet écran affiche le dernier rapport rangé,
 * daté, le relance, et met en face de chaque constat le bouton du geste qui
 * existe déjà dans l'écran. Il ne recalcule rien : gravités, phrases, état
 * des lots et « à relancer » viennent tels quels du serveur.
 *
 * Contrats :
 *   GET  /ao/:id/controle-qualite?besoin_id=   → dernier rapport (404 JSON : aucun rapport)
 *   POST /ao/:id/controle-qualite { besoin_id? } → { job_id } ou le rapport
 *   PUT  /ao/:id/nommage { dossier, piece_identite, permis, contrat }
 *   POST /ao/:id/pieces/renommer { besoin_id? }  → message (ou { job_id })
 * Une route absente (404 sans corps JSON, 501) est DITE « arrive côté
 * serveur » ; rien n'est écrit.
 */

const BASE = 'https://besoins-tat-production.up.railway.app';
const BI = ({ icon, style }) => <i className={`bi bi-${icon}`} style={style} aria-hidden="true" />;

const GRAVITES = {
  bloquant: { libelle: 'Bloquant', pluriel: 'Bloquants', icon: 'x-octagon', fg: 'var(--erp-danger)', bg: 'var(--erp-danger-soft)' },
  a_corriger: { libelle: 'À corriger', pluriel: 'À corriger', icon: 'exclamation-triangle', fg: 'var(--erp-warning)', bg: 'var(--erp-warning-soft)' },
  info: { libelle: 'Info', pluriel: 'Infos', icon: 'info-circle', fg: 'var(--erp-text-secondary)', bg: 'var(--erp-surface-muted)' },
};
const ETATS_LOT = {
  pret: { libelle: 'Prêt', fg: 'var(--erp-success)', bg: 'var(--erp-success-soft)', icon: 'check2' },
  a_corriger: { libelle: 'À corriger', fg: 'var(--erp-warning)', bg: 'var(--erp-warning-soft)', icon: 'exclamation-triangle' },
  bloque: { libelle: 'Bloqué', fg: 'var(--erp-danger)', bg: 'var(--erp-danger-soft)', icon: 'x-octagon' },
};

// Nom lisible d'un geste désigné par le serveur, quand l'écran ne sait pas
// le jouer lui-même : on dit le geste, on ne montre pas un identifiant.
const NOMS_GESTES = {
  ao_piece_generer: 'Générer la pièce',
  renommer_lot: 'Renommer le lot',
  ao_pieces_renommer: 'Renommer selon la nomenclature',
  candidat_retirer_du_besoin: 'Retirer du lot',
  detacher_lot: 'Détacher le lot',
  get_candidat: 'Ouvrir la fiche',
  candidat_lire: 'Ouvrir la fiche',
  get_besoin: 'Ouvrir le besoin',
  besoin_lire: 'Ouvrir le besoin',
  ao_livraison_copier: 'Livraison du dossier',
  ao_cv_regenerer_lot: 'Repasser les CV du dossier',
  cv_definir_pour_besoin: 'Choisir le CV du lot',
  grille_valider: 'Valider la grille',
};

const NOMMAGE_CHAMPS = [
  { cle: 'dossier', libelle: 'Dossier candidat', defaut: '{NOM} {Prénom}' },
  { cle: 'piece_identite', libelle: "Pièce d'identité", defaut: "{NOM} {Prénom} - Pièce d'identité.pdf" },
  { cle: 'permis', libelle: 'Permis', defaut: '{NOM} {Prénom} - Permis de travail.pdf' },
  { cle: 'contrat', libelle: 'Contrat', defaut: '{NOM} {Prénom} - Contrat de travail.pdf' },
];

const JOB_TITRE = 'Contrôle qualité en cours';
export const estJobControle = (jb) => !!jb && (jb._ecran === 'controle_qualite'
  || /controle[_ -]?qualite/i.test(String(jb.type || jb.kind || jb.nom || '')));

const lisRapport = (j) => {
  if (!j || typeof j !== 'object') return null;
  const r = j.rapport ?? j.controle_qualite ?? j.data ?? j.report ?? j;
  if (!r || typeof r !== 'object') return null;
  if (!r.resume && !Array.isArray(r.lots) && !Array.isArray(r.societe)) return null;
  return r;
};
const aRelancer = (r, j) => !!(r?.a_relancer ?? r?.perime ?? r?.stale ?? j?.a_relancer ?? j?.perime ?? j?.stale);
const dateCourte = (d) => {
  if (!d) return null;
  const t = new Date(d);
  if (Number.isNaN(t.getTime())) return null;
  const min = Math.max(0, Math.round((Date.now() - t.getTime()) / 60000));
  if (min < 1) return "à l'instant";
  if (min < 60) return `il y a ${min} min`;
  if (min < 1440) return `il y a ${Math.round(min / 60)} h`;
  return `le ${t.toLocaleDateString('fr-FR')}`;
};
const nb = (n) => (Number.isFinite(Number(n)) ? Number(n) : 0);

function Pastille({ g, children, title }) {
  const v = GRAVITES[g] || ETATS_LOT[g] || GRAVITES.info;
  return (
    <span title={title} style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '2px 8px', borderRadius: 'var(--erp-radius-xs)', background: v.bg, color: v.fg, fontSize: '11px', fontWeight: 600, whiteSpace: 'nowrap', flex: 'none' }}>
      <BI icon={v.icon} /> {children || v.libelle}
    </span>
  );
}

export default function AoControleQualite({
  aoId, nommage: nommageInitial, lotsScan = [], jobs = [], onJob, gestes = {}, showNotification, confirm,
}) {
  const authHeaders = useMemo(() => ({ Authorization: `Bearer ${APIService.getToken()}` }), []);
  const [rapport, setRapport] = useState(null);
  const [relancer, setRelancer] = useState(false);
  const [etat, setEtat] = useState('chargement');  // chargement | absent | indispo | ok | erreur
  const [erreur, setErreur] = useState('');
  const [ouvert, setOuvert] = useState(false);
  const [filtre, setFiltre] = useState('tous');
  const [lancement, setLancement] = useState(false);
  const [jobEnCours, setJobEnCours] = useState(null);
  const [lotsOuverts, setLotsOuverts] = useState(() => new Set());
  const [nommageOuvert, setNommageOuvert] = useState(false);
  const [nommage, setNommage] = useState({});
  const [nommageBusy, setNommageBusy] = useState(false);
  const [renommageLot, setRenommageLot] = useState('');
  const [renommageBusy, setRenommageBusy] = useState(false);

  useEffect(() => {
    const n = nommageInitial && typeof nommageInitial === 'object' ? nommageInitial : {};
    setNommage(Object.fromEntries(NOMMAGE_CHAMPS.map(c => [c.cle, n[c.cle] || ''])));
  }, [nommageInitial, aoId]);

  const charger = useCallback(async () => {
    if (aoId == null) return;
    try {
      const r = await fetch(`${BASE}/ao/${aoId}/controle-qualite`, { headers: authHeaders });
      const j = await r.json().catch(() => null);
      if (r.status === 501 || (r.status === 404 && !j)) { setEtat('indispo'); setRapport(null); return; }
      if (r.status === 404) { setEtat('absent'); setRapport(null); return; }
      if (!r.ok || j?.success === false) throw new Error(j?.error || j?.message || `HTTP ${r.status}`);
      const rap = lisRapport(j);
      setRapport(rap); setRelancer(aRelancer(rap, j));
      setEtat(rap ? 'ok' : 'absent');
    } catch (e) { setEtat('erreur'); setErreur(e.message); }
  }, [aoId, authHeaders]);

  useEffect(() => { setRapport(null); setEtat('chargement'); setJobEnCours(null); charger(); }, [charger]);

  // Une tâche lancée avant un rafraîchissement se retrouve dans la liste des
  // traitements actifs de l'écran : on la reprend.
  useEffect(() => {
    if (jobEnCours) return;
    const jb = jobs.find(estJobControle);
    if (jb) setJobEnCours(jb.id ?? jb.job_id);
  }, [jobs, jobEnCours]);
  // Fin de la tâche (elle quitte la liste des traitements actifs) : le
  // rapport rangé est relu.
  useEffect(() => {
    if (!jobEnCours) return;
    if (!jobs.some(jb => String(jb.id ?? jb.job_id) === String(jobEnCours))) {
      setJobEnCours(null); charger();
    }
  }, [jobs, jobEnCours, charger]);

  const lancer = async (besoinId = null) => {
    if (aoId == null || lancement || jobEnCours) return;
    setLancement(true);
    try {
      const r = await fetch(`${BASE}/ao/${aoId}/controle-qualite`, {
        method: 'POST', headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify(besoinId != null ? { besoin_id: besoinId } : {}),
      });
      const j = await r.json().catch(() => null);
      if (r.status === 501 || (r.status === 404 && !j)) {
        showNotification?.('Le contrôle qualité arrive côté serveur — rien n\'a été lancé.', 'info');
        setEtat(e => (e === 'chargement' ? 'indispo' : e));
        return;
      }
      if (!r.ok || j?.success === false) throw new Error(j?.error || j?.message || `HTTP ${r.status}`);
      const id = j?.job_id ?? j?.job?.id;
      if (id != null) {
        setJobEnCours(id);
        onJob?.({ job_id: id, job: { ...(j.job || {}), statut: j.job?.statut || 'queued', _ecran: 'controle_qualite', _titre: JOB_TITRE, label: j.job?.label || j.message || 'Lecture du dossier partagé…' } });
        showNotification?.(j?.message || 'Contrôle qualité lancé — le rapport s\'affiche à la fin.', 'success');
        return;
      }
      const rap = lisRapport(j);
      if (rap) { setRapport(rap); setRelancer(aRelancer(rap, j)); setEtat('ok'); setOuvert(true); }
      else charger();
    } catch (e) { showNotification?.(`Contrôle impossible : ${e.message}`, 'error'); }
    finally { setLancement(false); }
  };

  const enregistrerNommage = async () => {
    if (aoId == null) return;
    setNommageBusy(true);
    try {
      const corps = Object.fromEntries(NOMMAGE_CHAMPS.map(c => [c.cle, (nommage[c.cle] || '').trim() || null]));
      const r = await fetch(`${BASE}/ao/${aoId}/nommage`, {
        method: 'PUT', headers: { ...authHeaders, 'Content-Type': 'application/json' }, body: JSON.stringify(corps),
      });
      const j = await r.json().catch(() => null);
      if (r.status === 501 || (r.status === 404 && !j)) { showNotification?.('Le réglage de la nomenclature arrive côté serveur — rien n\'a été enregistré.', 'info'); return; }
      if (!r.ok || j?.success === false) throw new Error(j?.error || j?.message || `HTTP ${r.status}`);
      const n = j?.nommage || j?.data?.nommage;
      if (n && typeof n === 'object') setNommage(Object.fromEntries(NOMMAGE_CHAMPS.map(c => [c.cle, n[c.cle] || ''])));
      showNotification?.(j?.message || 'Nomenclature enregistrée.', 'success');
    } catch (e) { showNotification?.(`Enregistrement impossible : ${e.message}`, 'error'); }
    finally { setNommageBusy(false); }
  };

  const renommer = async (besoinId = null) => {
    if (aoId == null || renommageBusy) return;
    const lot = besoinId != null ? lotsScan.find(l => String(l.besoin_id) === String(besoinId)) : null;
    const ok = await (confirm ? confirm({
      title: 'Renommer selon la nomenclature',
      message: lot ? `Renommer les dossiers et les pièces du lot « ${lot.titre} » ?` : 'Renommer les dossiers candidats et les pièces de tout le dossier ?',
      detail: 'Écrit dans le dossier partagé : les dossiers candidats, pièces d\'identité, permis et contrats prennent le nom de la nomenclature, les images sont converties en PDF. Les fichiers rangés sur les fiches candidats ne sont pas touchés.',
      confirmLabel: 'Renommer',
    }) : Promise.resolve(window.confirm('Renommer les dossiers et les pièces selon la nomenclature ?')));
    if (!ok) return;
    setRenommageBusy(true);
    try {
      const r = await fetch(`${BASE}/ao/${aoId}/pieces/renommer`, {
        method: 'POST', headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify(besoinId != null ? { besoin_id: besoinId } : {}),
      });
      const j = await r.json().catch(() => null);
      if (r.status === 501 || (r.status === 404 && !j)) { showNotification?.('Le renommage selon la nomenclature arrive côté serveur — rien n\'a été modifié.', 'info'); return; }
      if (!r.ok || j?.success === false) throw new Error(j?.error || j?.message || `HTTP ${r.status}`);
      const id = j?.job_id ?? j?.job?.id;
      if (id != null) onJob?.({ job_id: id, job: { ...(j.job || {}), statut: j.job?.statut || 'queued', _titre: 'Renommage en cours', label: j.job?.label || j.message || 'Renommage…' } });
      showNotification?.(j?.message || 'Renommage terminé.', 'success');
      if (id == null) gestes.rescanner?.();
    } catch (e) { showNotification?.(`Renommage impossible : ${e.message}`, 'error'); }
    finally { setRenommageBusy(false); }
  };

  // Le geste désigné par le serveur, joué par le bouton qui existe déjà
  // dans l'écran. Sans équivalent ici : son nom seulement.
  const boutonGeste = (geste, ctx = {}) => {
    if (!geste || !geste.outil) return null;
    const e = geste.entree || {};
    const besoinId = e.besoin_id ?? ctx.besoin_id ?? null;
    const candidateId = e.candidate_id ?? e.candidat_id ?? ctx.candidate_id ?? null;
    let action = null; let libelle = NOMS_GESTES[geste.outil] || null; let icon = 'arrow-return-right';
    switch (geste.outil) {
      case 'ao_piece_generer':
        if (gestes.generer && besoinId != null && candidateId != null) { action = () => gestes.generer(besoinId, candidateId, e.piece || e.label || e.type || ''); icon = 'file-earmark-plus'; libelle = 'Générer'; }
        break;
      case 'get_candidat': case 'candidat_lire':
        if (gestes.ouvrirFiche && candidateId != null) { action = () => gestes.ouvrirFiche(candidateId); icon = 'person-badge'; libelle = 'Ouvrir la fiche'; }
        break;
      case 'get_besoin': case 'besoin_lire':
        if (gestes.ouvrirBesoin && besoinId != null) { action = () => gestes.ouvrirBesoin(besoinId); icon = 'briefcase'; }
        break;
      case 'candidat_retirer_du_besoin':
        if (gestes.retirer && besoinId != null && candidateId != null) { action = () => gestes.retirer(candidateId, besoinId, ctx.nom, ctx.poste); icon = 'person-dash'; }
        break;
      case 'renommer_lot':
        if (gestes.renommerLot && besoinId != null) { action = () => { setOuvert(false); gestes.renommerLot(besoinId, e.titre || e.nom || null); }; icon = 'pencil'; }
        break;
      case 'ao_pieces_renommer':
        action = () => renommer(besoinId); icon = 'input-cursor-text';
        break;
      default: break;
    }
    if (!action) {
      return (
        <span className="erp-chip" title="Geste à faire depuis l'écran concerné ou avec l'assistant">
          <BI icon="arrow-return-right" /> {libelle || geste.outil}
        </span>
      );
    }
    return (
      <button type="button" onClick={action} className="erp-btn erp-btn--secondary erp-btn--sm" style={{ flex: 'none' }}>
        <BI icon={icon} /> {libelle}
      </button>
    );
  };

  const garde = (c) => filtre === 'tous' || c?.gravite === filtre;
  const Constat = ({ c, ctx }) => (
    <li style={{ display: 'flex', gap: '10px', padding: '9px 0', borderTop: '1px solid var(--erp-line)', alignItems: 'flex-start' }}>
      <Pastille g={c.gravite} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--erp-ink-strong)' }}>{c.libelle || c.code}</div>
        {c.detail && <div style={{ marginTop: '2px', fontSize: '12.5px', color: 'var(--erp-text-secondary)', overflowWrap: 'anywhere' }}>{c.detail}</div>}
        {c.quoi_faire && <div style={{ marginTop: '4px', fontSize: '12.5px', color: 'var(--erp-ink)' }}><BI icon="arrow-return-right" style={{ marginRight: '5px', color: 'var(--erp-text-tertiary)' }} />{c.quoi_faire}</div>}
        {c.geste && <div style={{ marginTop: '7px' }}>{boutonGeste(c.geste, ctx)}</div>}
      </div>
    </li>
  );

  const resume = rapport?.resume || {};
  const lots = Array.isArray(rapport?.lots) ? rapport.lots : [];
  const societe = (Array.isArray(rapport?.societe) ? rapport.societe : []).filter(garde);
  const nonLus = Array.isArray(rapport?.non_lus) ? rapport.non_lus : [];
  const dateCtrl = dateCourte(rapport?.controle_le);
  const enCours = lancement || !!jobEnCours;

  const lotsVus = lots.map(l => {
    const constats = (l.constats || []).filter(garde);
    const candidats = (l.candidats || []).map(c => ({ ...c, _vus: (c.constats || []).filter(garde) }))
      .filter(c => filtre === 'tous' || c._vus.length);
    return { ...l, _constats: constats, _candidats: candidats };
  }).filter(l => filtre === 'tous' || l._constats.length || l._candidats.length);

  const basculeLot = (k) => setLotsOuverts(prev => { const n = new Set(prev); if (n.has(k)) n.delete(k); else n.add(k); return n; });

  const filtres = [
    ['tous', 'Tout', null],
    ['bloquant', 'Bloquants', resume.bloquants],
    ['a_corriger', 'À corriger', resume.a_corriger],
    ['info', 'Infos', resume.infos],
  ];

  return (
    <>
      <button type="button" {...ancre('ao-controle-qualite')}
        onClick={() => setOuvert(true)}
        title="Ce qui partirait chez le client par erreur et ce qui manque, lot par lot — lu dans le dossier partagé, sans rien modifier"
        className="erp-btn erp-btn--secondary"
        style={{ alignSelf: 'center' }}>
        <BI icon={enCours ? 'arrow-repeat' : 'clipboard-check'} style={enCours ? { animation: 'ao-spin .9s linear infinite' } : undefined} />
        Contrôle qualité
        {etat === 'ok' && nb(resume.bloquants) > 0 && (
          <span title={`${nb(resume.bloquants)} bloquant(s) au dernier contrôle`} style={{ padding: '0 7px', borderRadius: '20px', background: 'var(--erp-danger-soft)', color: 'var(--erp-danger)', fontSize: '11px', fontWeight: 700 }}>{nb(resume.bloquants)}</span>
        )}
        {etat === 'ok' && relancer && (
          <span title="Le dossier a été relu depuis ce contrôle : relancez-le" style={{ padding: '0 7px', borderRadius: '20px', background: 'var(--erp-warning-soft)', color: 'var(--erp-warning)', fontSize: '11px', fontWeight: 600 }}>à relancer</span>
        )}
      </button>
      {etat === 'ok' && dateCtrl && (
        <span title="Date du dernier contrôle qualité" style={{ fontSize: '11.5px', color: 'var(--erp-text-tertiary)', alignSelf: 'center' }}>contrôlé {dateCtrl}</span>
      )}

      {ouvert && (
        <div onClick={() => setOuvert(false)} style={{ position: 'fixed', inset: 0, zIndex: 590, background: 'var(--erp-overlay)' }}>
          <aside className="erp-drawer" role="dialog" aria-label="Contrôle qualité" onClick={e => e.stopPropagation()}
            style={{ position: 'absolute', top: 0, right: 0, bottom: 0, width: 'min(720px, 100vw)', overflowY: 'auto', padding: '18px 18px 28px', boxSizing: 'border-box' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', marginBottom: '14px' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <h2 style={{ margin: 0, fontFamily: "'Archivo',sans-serif", fontWeight: 800, fontSize: '21px', letterSpacing: '-0.02em', color: 'var(--erp-ink-strong)' }}>Contrôle qualité</h2>
                <p style={{ margin: '4px 0 0', fontSize: '12.5px', color: 'var(--erp-text-secondary)' }}>
                  Avant d'envoyer : ce qui partirait par erreur et ce qui manque. Le contrôle lit le dossier partagé, il ne modifie rien.
                  {dateCtrl && <> Dernier contrôle {dateCtrl}{rapport?.duree_ms ? ` (${Math.round(nb(rapport.duree_ms) / 1000)} s)` : ''}.</>}
                </p>
              </div>
              <button type="button" onClick={() => setOuvert(false)} className="erp-btn erp-btn--secondary erp-btn--sm" aria-label="Fermer"><BI icon="x-lg" /></button>
            </div>

            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '14px' }}>
              <button type="button" onClick={() => lancer()} disabled={enCours || etat === 'indispo'} className="erp-btn erp-btn--primary">
                <BI icon={enCours ? 'arrow-repeat' : 'clipboard-check'} style={enCours ? { animation: 'ao-spin .9s linear infinite' } : undefined} />
                {enCours ? 'Contrôle en cours…' : (rapport ? 'Relancer le contrôle' : 'Lancer le contrôle')}
              </button>
              <button type="button" onClick={() => setNommageOuvert(v => !v)} className="erp-btn erp-btn--secondary" aria-expanded={nommageOuvert}>
                <BI icon="input-cursor-text" /> Nomenclature des pièces
              </button>
            </div>

            {relancer && etat === 'ok' && (
              <div style={{ marginBottom: '12px', padding: '9px 12px', borderRadius: 'var(--erp-radius-sm)', background: 'var(--erp-warning-soft)', color: 'var(--erp-warning)', fontSize: '12.5px' }}>
                <BI icon="exclamation-triangle" style={{ marginRight: '6px' }} />Le dossier a été relu depuis ce contrôle : ce rapport peut ne plus être à jour. Relancez-le.
              </div>
            )}

            {nommageOuvert && (
              <section className="erp-card" style={{ padding: '13px 15px', marginBottom: '14px' }}>
                <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--erp-ink-strong)' }}>Nomenclature du dossier partagé</div>
                <p style={{ margin: '3px 0 10px', fontSize: '12px', color: 'var(--erp-text-secondary)' }}>
                  Les noms que prennent les dossiers candidats et les pièces copiés dans le dossier partagé. Variables :
                  {' '}<code style={{ fontFamily: 'var(--erp-font-mono)' }}>{'{NOM}'}</code> (capitales),
                  {' '}<code style={{ fontFamily: 'var(--erp-font-mono)' }}>{'{Prénom}'}</code>,
                  {' '}<code style={{ fontFamily: 'var(--erp-font-mono)' }}>{'{piece}'}</code>. Laissé vide, le nom grisé s'applique.
                </p>
                <div style={{ display: 'grid', gridTemplateColumns: 'minmax(110px, 150px) 1fr', gap: '8px 10px', alignItems: 'center' }}>
                  {NOMMAGE_CHAMPS.map(c => (
                    <React.Fragment key={c.cle}>
                      <label htmlFor={`ao-nommage-${c.cle}`} style={{ fontSize: '12.5px', color: 'var(--erp-ink)' }}>{c.libelle}</label>
                      <input id={`ao-nommage-${c.cle}`} className="erp-field" value={nommage[c.cle] || ''} placeholder={c.defaut}
                        onChange={ev => setNommage(n => ({ ...n, [c.cle]: ev.target.value }))}
                        style={{ fontFamily: 'var(--erp-font-mono)', fontSize: '12.5px', minWidth: 0 }} />
                    </React.Fragment>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '12px', alignItems: 'center' }}>
                  <button type="button" onClick={enregistrerNommage} disabled={nommageBusy} className="erp-btn erp-btn--primary erp-btn--sm">
                    <BI icon={nommageBusy ? 'arrow-repeat' : 'check2'} style={nommageBusy ? { animation: 'ao-spin .9s linear infinite' } : undefined} /> Enregistrer la nomenclature
                  </button>
                  <span style={{ flex: 1 }} />
                  <select className="erp-field" value={renommageLot} onChange={ev => setRenommageLot(ev.target.value)} aria-label="Portée du renommage"
                    style={{ fontSize: '12.5px', maxWidth: '230px', minHeight: 0, paddingTop: '4px', paddingBottom: '4px' }}>
                    <option value="">Tout le dossier</option>
                    {lotsScan.map(l => <option key={l.besoin_id} value={l.besoin_id}>{l.titre}</option>)}
                  </select>
                  <button type="button" onClick={() => renommer(renommageLot ? renommageLot : null)} disabled={renommageBusy} className="erp-btn erp-btn--secondary erp-btn--sm">
                    <BI icon={renommageBusy ? 'arrow-repeat' : 'input-cursor-text'} style={renommageBusy ? { animation: 'ao-spin .9s linear infinite' } : undefined} /> Renommer selon la nomenclature
                  </button>
                </div>
              </section>
            )}

            {etat === 'chargement' && (
              <p style={{ fontSize: '12.5px', color: 'var(--erp-text-secondary)' }}><BI icon="arrow-repeat" style={{ animation: 'ao-spin .9s linear infinite', marginRight: '6px' }} />Lecture du dernier rapport…</p>
            )}
            {etat === 'indispo' && (
              <div className="erp-card" style={{ padding: '16px', fontSize: '12.5px', color: 'var(--erp-text-secondary)' }}>
                <BI icon="info-circle" style={{ marginRight: '6px' }} />Le contrôle qualité arrive côté serveur — il n'est pas encore disponible sur ce dossier.
              </div>
            )}
            {etat === 'erreur' && (
              <div style={{ padding: '10px 12px', borderRadius: 'var(--erp-radius-sm)', background: 'var(--erp-danger-soft)', color: 'var(--erp-danger)', fontSize: '12.5px' }}>
                {erreur} <button type="button" onClick={charger} className="erp-btn erp-btn--secondary erp-btn--sm" style={{ marginLeft: '8px' }}>Réessayer</button>
              </div>
            )}
            {etat === 'absent' && !enCours && (
              <div className="erp-card" style={{ padding: '22px 18px', textAlign: 'center' }}>
                <BI icon="clipboard-check" style={{ fontSize: '22px', color: 'var(--erp-text-tertiary)' }} />
                <p style={{ margin: '8px 0 12px', fontSize: '13px', color: 'var(--erp-text-secondary)' }}>Aucun contrôle sur ce dossier. Lancez-le avant d'envoyer : il relève les dossiers en trop, les pièces d'un autre lot, ce qui manque et la marge.</p>
                <button type="button" onClick={() => lancer()} className="erp-btn erp-btn--primary"><BI icon="clipboard-check" /> Lancer le contrôle</button>
              </div>
            )}
            {etat === 'absent' && enCours && (
              <p style={{ fontSize: '12.5px', color: 'var(--erp-text-secondary)' }}><BI icon="arrow-repeat" style={{ animation: 'ao-spin .9s linear infinite', marginRight: '6px' }} />Contrôle en cours — la progression s'affiche sur l'écran du dossier, le rapport ici à la fin.</p>
            )}

            {etat === 'ok' && rapport && (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '8px', marginBottom: '14px' }}>
                  {[
                    { k: 'Bloquants', v: nb(resume.bloquants), fg: nb(resume.bloquants) ? 'var(--erp-danger)' : 'var(--erp-ink-strong)' },
                    { k: 'À corriger', v: nb(resume.a_corriger), fg: nb(resume.a_corriger) ? 'var(--erp-warning)' : 'var(--erp-ink-strong)' },
                    { k: 'Lots prêts', v: `${nb(resume.lots_prets)}/${nb(resume.lots_total ?? lots.length)}`, fg: 'var(--erp-ink-strong)' },
                  ].map(s => (
                    <div key={s.k} className="erp-card" style={{ padding: '11px 13px' }}>
                      <div style={{ fontFamily: "'Archivo',sans-serif", fontWeight: 800, fontSize: '23px', letterSpacing: '-0.03em', lineHeight: 1, color: s.fg }}>{s.v}</div>
                      <div style={{ marginTop: '4px', fontSize: '10.5px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--erp-text-tertiary)' }}>{s.k}</div>
                    </div>
                  ))}
                </div>

                <div role="tablist" aria-label="Filtrer par gravité" style={{ display: 'flex', gap: '4px', borderBottom: '1px solid var(--erp-line)', marginBottom: '12px', flexWrap: 'wrap' }}>
                  {filtres.map(([id, lbl, n]) => (
                    <button key={id} type="button" role="tab" aria-selected={filtre === id} onClick={() => setFiltre(id)}
                      style={{ padding: '7px 11px', border: 'none', background: 'transparent', cursor: 'pointer', fontFamily: 'inherit', fontSize: '12.5px', fontWeight: 600, color: filtre === id ? 'var(--erp-ink-strong)' : 'var(--erp-text-tertiary)', borderBottom: filtre === id ? '2px solid var(--erp-accent)' : '2px solid transparent', marginBottom: '-1px' }}>
                      {lbl}{n != null ? ` (${nb(n)})` : ''}
                    </button>
                  ))}
                </div>

                {societe.length > 0 && (
                  <section className="erp-card" style={{ padding: '11px 14px', marginBottom: '10px' }}>
                    <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--erp-ink-strong)', marginBottom: '2px' }}><BI icon="building" style={{ marginRight: '6px' }} />Administratif société</div>
                    <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>{societe.map((c, i) => <Constat key={`s${i}`} c={c} ctx={{}} />)}</ul>
                  </section>
                )}

                {lotsVus.length === 0 && (
                  <p style={{ fontSize: '12.5px', color: 'var(--erp-text-secondary)' }}>{filtre === 'tous' ? 'Aucun lot dans ce rapport.' : 'Aucun constat de cette gravité.'}</p>
                )}
                {lotsVus.map(l => {
                  const k = String(l.besoin_id ?? l.reference_interne ?? l.titre);
                  const ouvertLot = lotsOuverts.has(k) || filtre !== 'tous';
                  const total = l._constats.length + l._candidats.reduce((s, c) => s + c._vus.length, 0);
                  const poste = l.reference_interne ? `${l.reference_interne} — ${l.titre || ''}` : (l.titre || `Lot ${l.besoin_id}`);
                  return (
                    <section key={k} className="erp-card" style={{ marginBottom: '8px', overflow: 'hidden' }}>
                      <button type="button" onClick={() => basculeLot(k)} aria-expanded={ouvertLot}
                        style={{ display: 'flex', width: '100%', alignItems: 'center', gap: '9px', padding: '11px 14px', border: 'none', background: 'transparent', cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left' }}>
                        <BI icon={ouvertLot ? 'chevron-down' : 'chevron-right'} style={{ color: 'var(--erp-text-tertiary)' }} />
                        {l.reference_interne && <span style={{ fontFamily: 'var(--erp-font-mono)', fontSize: '11.5px', color: 'var(--erp-text-secondary)', flex: 'none' }}>{l.reference_interne}</span>}
                        <span style={{ flex: 1, minWidth: 0, fontSize: '13px', fontWeight: 600, color: 'var(--erp-ink-strong)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.titre || `Lot ${l.besoin_id}`}</span>
                        {total > 0 && <span style={{ fontFamily: 'var(--erp-font-mono)', fontSize: '11.5px', color: 'var(--erp-text-tertiary)', flex: 'none' }}>{total} constat{total > 1 ? 's' : ''}</span>}
                        {ETATS_LOT[l.etat] && <Pastille g={l.etat} />}
                      </button>
                      {ouvertLot && (
                        <div style={{ padding: '0 14px 10px' }}>
                          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '4px' }}>
                            {gestes.ouvrirLot && l.besoin_id != null && (
                              <button type="button" onClick={() => { setOuvert(false); gestes.ouvrirLot(l.besoin_id); }} className="erp-btn erp-btn--secondary erp-btn--sm"><BI icon="folder2-open" /> Voir le lot</button>
                            )}
                            {l.besoin_id != null && (
                              <button type="button" onClick={() => lancer(l.besoin_id)} disabled={enCours} className="erp-btn erp-btn--secondary erp-btn--sm"><BI icon="clipboard-check" /> Recontrôler ce lot</button>
                            )}
                          </div>
                          {l._constats.length > 0 && (
                            <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>{l._constats.map((c, i) => <Constat key={`l${i}`} c={c} ctx={{ besoin_id: l.besoin_id, poste }} />)}</ul>
                          )}
                          {l._candidats.map(c => (
                            <div key={c.candidate_id ?? c.nom} style={{ marginTop: '8px', paddingLeft: '10px', borderLeft: '2px solid var(--erp-line)' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', padding: '4px 0' }}>
                                <BI icon="person" style={{ color: 'var(--erp-text-tertiary)' }} />
                                <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--erp-ink-strong)' }}>{c.nom || `Candidat ${c.candidate_id}`}</span>
                                {c.proposable === false && <span className="erp-chip" title="Ce profil n'est pas proposé au client sur ce lot">non proposable</span>}
                                {c._vus.length === 0 && <Pastille g="pret">Rien à signaler</Pastille>}
                                <span style={{ flex: 1 }} />
                                {gestes.ouvrirFiche && c.candidate_id != null && (
                                  <button type="button" onClick={() => gestes.ouvrirFiche(c.candidate_id)} className="erp-btn erp-btn--secondary erp-btn--sm"><BI icon="person-badge" /> Fiche</button>
                                )}
                              </div>
                              {c._vus.length > 0 && (
                                <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>{c._vus.map((x, i) => <Constat key={`c${i}`} c={x} ctx={{ besoin_id: l.besoin_id, candidate_id: c.candidate_id, nom: c.nom, poste }} />)}</ul>
                              )}
                            </div>
                          ))}
                          {total === 0 && filtre === 'tous' && (l.candidats || []).length === 0 && (
                            <p style={{ margin: '6px 0 0', fontSize: '12.5px', color: 'var(--erp-text-secondary)' }}>Aucun constat sur ce lot.</p>
                          )}
                        </div>
                      )}
                    </section>
                  );
                })}

                {nonLus.length > 0 && (
                  <section className="erp-card" style={{ padding: '11px 14px', marginTop: '12px' }}>
                    <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--erp-ink-strong)' }}><BI icon="file-earmark-x" style={{ marginRight: '6px' }} />Fichiers non lus ({nonLus.length})</div>
                    <p style={{ margin: '2px 0 6px', fontSize: '12px', color: 'var(--erp-text-secondary)' }}>Le contrôle n'a pas pu les lire : ils n'ont pas été vérifiés.</p>
                    <ul style={{ margin: 0, paddingLeft: '18px' }}>
                      {nonLus.map((n, i) => (
                        <li key={i} style={{ fontSize: '12px', color: 'var(--erp-ink)', marginBottom: '3px', overflowWrap: 'anywhere' }}>
                          <span style={{ fontFamily: 'var(--erp-font-mono)' }}>{n.chemin}</span>{n.raison ? ` — ${n.raison}` : ''}
                        </li>
                      ))}
                    </ul>
                  </section>
                )}
              </>
            )}
          </aside>
        </div>
      )}
    </>
  );
}
